import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, SelectionChangedEvent, ModuleRegistry, AllCommunityModule } from 'ag-grid-community'

// Register AG-Grid modules
ModuleRegistry.registerModules([AllCommunityModule])
import PoolAdminRoute from '../components/PoolAdminRoute'
import LoadingSpinner from '../components/LoadingSpinner'
import { useTheme } from '../contexts/ThemeContext'
import { 
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Game {
  id: number
  season: number
  week: number
  seasonType: string
  startDate: string
  homeTeamId: number
  homeTeam: string
  homeConference: string
  awayTeamId: number
  awayTeam: string
  awayConference: string
  venue: string
  venueId?: number
  conferenceGame: boolean
  lines: any[]
  mediaOutlets: string[]
  isStartTimeTBD: boolean
  completed?: boolean
  neutralSite?: boolean
  homeTeamRankings?: {
    ap?: {
      rank: number
      school: string
      conference: string
      points: number
      firstPlaceVotes: number
      poll: string
    } | null
    coaches?: {
      rank: number
      school: string
      conference: string
      points: number
      firstPlaceVotes: number
      poll: string
    } | null
  }
  awayTeamRankings?: {
    ap?: {
      rank: number
      school: string
      conference: string
      points: number
      firstPlaceVotes: number
      poll: string
    } | null
    coaches?: {
      rank: number
      school: string
      conference: string
      points: number
      firstPlaceVotes: number
      poll: string
    } | null
  }
  homeTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
  awayTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
}

interface CreatePoolData {
  name: string
  description: string
  maxParticipants: number
  entryFee: number
  selectedGames: number[]
}

export default function CreateFootballPoolPage() {
  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const { isDark } = useTheme()
  
  const [step, setStep] = useState(1) // 1: Pool Details, 2: Select Date, 3: Select Games, 4: Review
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Pool form data
  const [poolData, setPoolData] = useState<CreatePoolData>({
    name: '',
    description: '',
    maxParticipants: 50,
    entryFee: 20,
    selectedGames: []
  })
  
  // Games data
  const [gameDate, setGameDate] = useState('')
  const [availableGames, setAvailableGames] = useState<Game[]>([])
  const [loadingGames, setLoadingGames] = useState(false)
  
  // AG-Grid refs (removed unused refs to fix linting)

  // Get current date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0]

  const handlePoolDataChange = (field: keyof CreatePoolData, value: any) => {
    setPoolData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const fetchGames = async (date: string) => {
    console.log('fetchGames called with date:', date)
    if (!date) {
      console.log('No date provided, returning')
      return
    }
    
    try {
      setLoadingGames(true)
      setError(null)
      
      console.log('Getting access token...')
      const token = await getAccessTokenSilently()
      console.log('Token received, making API call to combined endpoint...')
      
      // Use the combined endpoint that returns ready-to-use data
      const response = await fetch(
        `${API_BASE_URL}/college-football-data/combined-games?gameDate=${date}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch games data: ${response.statusText}`)
      }

      const data = await response.json()
      const games = data.games || []
      
      console.log(`Received ${games.length} games from combined endpoint`)
      
      // Use the games data directly - backend already combined everything
      setAvailableGames(games)
      
      console.log('Games data successfully loaded')
    } catch (err) {
      console.error('Error fetching games:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch games')
    } finally {
      setLoadingGames(false)
    }
  }

  // Note: fetchMediaData is no longer needed since we use the combined endpoint
  // that returns both lines and media data together


  const createPool = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getAccessTokenSilently()
      
      // Get selected games details
      const selectedGamesData = availableGames.filter(game => 
        poolData.selectedGames.includes(game.id)
      )

      const poolPayload = {
        name: poolData.name,
        description: poolData.description,
        type: 'football',
        startDate: gameDate, // Use the selected game date as pool start date
        endDate: gameDate,   // Single day pool for now
        maxParticipants: poolData.maxParticipants,
        entryFee: poolData.entryFee,
        games: selectedGamesData.map(game => ({
          gameId: game.id,
          homeTeam: game.homeTeam,
          awayTeam: game.awayTeam,
          homeConference: game.homeConference,
          awayConference: game.awayConference,
          startDate: game.startDate,
          week: game.week,
          seasonType: game.seasonType,
          venue: game.venue,
          completed: game.completed || false,
          neutralSite: game.neutralSite || false,
          conferenceGame: game.conferenceGame,
          mediaOutlets: game.mediaOutlets || [],
          lines: game.lines || [],
          homeTeamRankings: game.homeTeamRankings || { ap: null, coaches: null },
          awayTeamRankings: game.awayTeamRankings || { ap: null, coaches: null },
          homeTeamRecord: game.homeTeamRecord || null,
          awayTeamRecord: game.awayTeamRecord || null
        }))
      }

      const response = await fetch(`${API_BASE_URL}/pools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(poolPayload)
      })

      if (!response.ok) {
        throw new Error(`Failed to create pool: ${response.statusText}`)
      }

      await response.json()
      // TODO: Create pool detail/edit page
      navigate('/admin/pools/football')
    } catch (err) {
      console.error('Error creating pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to create pool')
    } finally {
      setLoading(false)
    }
  }

  const formatGameTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Removed formatGameDate as it's not currently used


  // AG-Grid column definitions (v34+ compatible) - memoized to refresh when data changes
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Time',
      field: 'startDate',
      width: 90,
      cellRenderer: (params: any) => {
        if (!params.value) return 'TBD'
        try {
          return formatGameTime(params.value)
        } catch (e) {
          return 'TBD'
        }
      },
      sort: 'asc'
    },
    {
      headerName: 'Away Team',
      field: 'awayTeam',
      width: 200,
      cellRenderer: (params: any) => {
        const teamName = params.value
        const rankings = params.data.awayTeamRankings
        const apRank = rankings?.ap?.rank
        const lines = params.data.lines || []
        
        // Get the best available spread (prefer DraftKings, fallback to ESPN)
        const dkLine = lines.find((line: any) => line.provider === 'DraftKings')
        const espnLine = lines.find((line: any) => line.provider === 'ESPN Bet')
        const bestLine = dkLine || espnLine
        
        let spreadText = ''
        if (bestLine && bestLine.spread !== null && bestLine.spread !== undefined) {
          const spread = bestLine.spread
          const awaySpread = -spread // Away team gets opposite of the spread
          const sign = awaySpread > 0 ? '+' : ''
          spreadText = ` (${sign}${awaySpread})`
        }
        
        return (
          <div className="flex items-center">
            {apRank && (
              <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                {apRank}
              </span>
            )}
            <span>
              {teamName}{spreadText}
              {params.data.awayTeamRecord && (
                <span className="text-sm text-gray-500 ml-1">
                  ({params.data.awayTeamRecord.wins}-{params.data.awayTeamRecord.losses})
                </span>
              )}
            </span>
          </div>
        )
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Home Team', 
      field: 'homeTeam',
      width: 200,
      cellRenderer: (params: any) => {
        const teamName = params.value
        const rankings = params.data.homeTeamRankings
        const apRank = rankings?.ap?.rank
        const lines = params.data.lines || []
        
        // Get the best available spread (prefer DraftKings, fallback to ESPN)
        const dkLine = lines.find((line: any) => line.provider === 'DraftKings')
        const espnLine = lines.find((line: any) => line.provider === 'ESPN Bet')
        const bestLine = dkLine || espnLine
        
        let spreadText = ''
        if (bestLine && bestLine.spread !== null && bestLine.spread !== undefined) {
          const spread = bestLine.spread
          const sign = spread > 0 ? '+' : ''
          spreadText = ` (${sign}${spread})`
        }
        
        return (
          <div className="flex items-center">
            {apRank && (
              <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                {apRank}
              </span>
            )}
            <span>
              {teamName}{spreadText}
              {params.data.homeTeamRecord && (
                <span className="text-sm text-gray-500 ml-1">
                  ({params.data.homeTeamRecord.wins}-{params.data.homeTeamRecord.losses})
                </span>
              )}
            </span>
          </div>
        )
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'DraftKings Spread',
      field: 'lines',
      width: 180,
      valueFormatter: (params: any) => {
        if (!params.value || params.value.length === 0) return 'N/A'
        const dkLine = params.value.find((line: any) => line.provider === 'DraftKings')
        if (!dkLine || dkLine.spread === null || dkLine.spread === undefined) return 'N/A'
        
        const spread = dkLine.spread
        const homeTeam = params.data.homeTeam
        
        if (spread > 0) {
          // Home team is underdog
          return `${homeTeam} +${spread}`
        } else if (spread < 0) {
          // Home team is favorite
          return `${homeTeam} ${spread}`
        } else {
          // Pick'em
          return `${homeTeam} PK`
        }
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'ESPN Spread',
      field: 'lines',
      width: 180,
      valueFormatter: (params: any) => {
        if (!params.value || params.value.length === 0) return 'N/A'
        const espnLine = params.value.find((line: any) => line.provider === 'ESPN Bet')
        if (!espnLine || espnLine.spread === null || espnLine.spread === undefined) return 'N/A'
        
        const spread = espnLine.spread
        const homeTeam = params.data.homeTeam
        
        if (spread > 0) {
          // Home team is underdog
          return `${homeTeam} +${spread}`
        } else if (spread < 0) {
          // Home team is favorite
          return `${homeTeam} ${spread}`
        } else {
          // Pick'em
          return `${homeTeam} PK`
        }
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'DK O/U',
      field: 'lines',
      width: 100,
      valueFormatter: (params: any) => {
        if (!params.value || params.value.length === 0) return 'N/A'
        const dkLine = params.value.find((line: any) => line.provider === 'DraftKings')
        return dkLine?.overUnder ? `O/U ${dkLine.overUnder}` : 'N/A'
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'ESPN O/U',
      field: 'lines',
      width: 100,
      valueFormatter: (params: any) => {
        if (!params.value || params.value.length === 0) return 'N/A'
        const espnLine = params.value.find((line: any) => line.provider === 'ESPN Bet')
        return espnLine?.overUnder ? `O/U ${espnLine.overUnder}` : 'N/A'
      },
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'TV',
      field: 'mediaOutlets',
      width: 100,
      valueFormatter: (params: any) => {
        if (!params.value || params.value.length === 0) return 'TBD'
        return params.value[0]
      },
      filter: 'agTextColumnFilter'
    }
  ], [])

  const onGridReady = useCallback((params: any) => {
    console.log('AG-Grid ready, row count:', params.api.getDisplayedRowCount())
    console.log('Available games:', availableGames.length)
    // Auto-size columns to fit
    params.api.sizeColumnsToFit()
    
    // Auto-size columns to fit and refresh display
    if (params.api) {
      setTimeout(() => {
        params.api.sizeColumnsToFit()
        params.api.refreshCells()
        params.api.redrawRows()
      }, 200)
    }
  }, [availableGames])

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows()
    const selectedIds = selectedRows.map(row => row.id)
    setPoolData(prev => ({
      ...prev,
      selectedGames: selectedIds
    }))
  }, [])

  const canProceedFromStep1 = poolData.name && poolData.description
  const canProceedFromStep2 = gameDate && availableGames.length > 0
  const canProceedFromStep3 = poolData.selectedGames.length > 0

  return (
    <PoolAdminRoute>
      <div className={`${step === 3 || step === 4 ? 'flex flex-col px-4 py-4' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}`} style={step === 3 || step === 4 ? { height: 'calc(100vh - 80px)' } : {}}>
        {/* Header */}
        <div className={step === 3 || step === 4 ? "mb-4" : "mb-8"}>
          <h1 className={`${step === 3 || step === 4 ? 'text-xl' : 'text-2xl'} font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl`}>
            Create Football Pool
          </h1>
          {step !== 3 && step !== 4 && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Set up a new football pool with game selection and participant management
            </p>
          )}
        </div>

        {/* Progress Steps */}
        <div className={step === 3 || step === 4 ? "mb-4" : "mb-8"}>
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {[
                { id: 1, name: 'Pool Details', description: 'Basic information' },
                { id: 2, name: 'Select Date', description: 'Choose game date' },
                { id: 3, name: 'Select Games', description: 'Pick games and create pool' }
              ].map((stepItem, stepIdx) => (
                <li key={stepItem.id} className={`${stepIdx !== 2 ? 'pr-8 sm:pr-20' : ''} relative`}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center ${step === 3 ? 'w-8 h-8' : 'w-10 h-10'} rounded-full border-2 ${
                      step > stepItem.id 
                        ? 'bg-green-600 border-green-600' 
                        : step === stepItem.id 
                        ? 'border-green-600 bg-white dark:bg-gray-800' 
                        : 'border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600'
                    }`}>
                      {step > stepItem.id ? (
                        <CheckCircleIcon className={`${step === 3 ? 'w-5 h-5' : 'w-6 h-6'} text-white`} />
                      ) : (
                        <span className={`${step === 3 ? 'text-xs' : 'text-sm'} font-medium ${
                          step === stepItem.id 
                            ? 'text-green-600' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {stepItem.id}
                        </span>
                      )}
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className={`${step === 3 ? 'text-xs' : 'text-sm'} font-medium ${
                        step >= stepItem.id 
                          ? 'text-gray-900 dark:text-white' 
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {stepItem.name}
                      </p>
                      {step !== 3 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {stepItem.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {stepIdx !== 2 && (
                    <div className="absolute top-5 right-0 w-5 h-0.5 bg-gray-300 dark:bg-gray-600" />
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        )}

        {/* Step Content */}
        <div className={`bg-white dark:bg-gray-800 shadow rounded-lg p-6 ${step === 3 || step === 4 ? 'flex-1 flex flex-col min-h-0' : ''}`}>
          {/* Step 1: Pool Details */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Pool Information</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pool Name *
                  </label>
                  <input
                    type="text"
                    value={poolData.name}
                    onChange={(e) => handlePoolDataChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Week 1 College Football Pool"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={poolData.description}
                    onChange={(e) => handlePoolDataChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Describe the pool rules and format..."
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max Participants
                    </label>
                    <input
                      type="number"
                      value={poolData.maxParticipants}
                      onChange={(e) => handlePoolDataChange('maxParticipants', parseInt(e.target.value))}
                      min="1"
                      max="200"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Entry Fee ($)
                    </label>
                    <input
                      type="number"
                      value={poolData.entryFee}
                      onChange={(e) => handlePoolDataChange('entryFee', parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Date */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Select Game Date</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Game Date *
                  </label>
                  <input
                    type="date"
                    value={gameDate}
                    onChange={(e) => {
                      console.log('Date input changed:', e.target.value)
                      setGameDate(e.target.value)
                      if (e.target.value) {
                        console.log('Calling fetchGames with:', e.target.value)
                        fetchGames(e.target.value)
                      } else {
                        console.log('No date value, not calling fetchGames')
                      }
                    }}
                    min={today}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Select the date to fetch available college football games
                  </p>
                </div>

                {loadingGames && (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                )}

                {gameDate && !loadingGames && availableGames.length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-gray-500 dark:text-gray-400">
                      No games found for {gameDate}. Try selecting a different date.
                    </div>
                  </div>
                )}

                {availableGames.length > 0 && (
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400 mb-4">
                      Found {availableGames.length} games for {gameDate}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Select Games */}
          {step === 3 && (
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex-shrink-0">
                Select Games ({poolData.selectedGames.length} selected)
              </h3>
              
              {availableGames.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No games loaded. Check the console for debug information.
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  {/* Status info */}
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex-shrink-0">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      {availableGames.length} games available • {poolData.selectedGames.length} selected
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                      {availableGames.length > 0 && (
                        <>
                          Sample: {availableGames[0]?.awayTeamRankings?.ap?.rank && `#${availableGames[0].awayTeamRankings.ap.rank} `}
                          {availableGames[0]?.awayTeam} @ {availableGames[0]?.homeTeamRankings?.ap?.rank && `#${availableGames[0].homeTeamRankings.ap.rank} `}
                          {availableGames[0]?.homeTeam}
                        </>
                      )}
                    </p>
                  </div>

                  {/* AG-Grid Table */}
                  <div 
                    className={`ag-theme-alpine flex-1 ${isDark ? 'dark' : ''}`}
                    style={{ 
                      width: '100%',
                      minHeight: 0
                    }}
                  >
                    <AgGridReact
                      rowData={availableGames}
                      columnDefs={columnDefs}
                      onGridReady={onGridReady}
                      onSelectionChanged={onSelectionChanged}
                      rowSelection={{
                        mode: 'multiRow',
                        checkboxes: true,
                        headerCheckbox: true,
                        enableClickSelection: false
                      }}
                      animateRows={true}
                      getRowId={(params) => params.data.id.toString()}
                      defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        minWidth: 100,
                        flex: 1
                      }}
                      pagination={false}
                      domLayout="normal"
                      enableCellTextSelection={true}
                      headerHeight={36}
                      rowHeight={32}
                      theme="legacy"
                      suppressHorizontalScroll={true}
                    />
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Navigation Buttons */}
          <div className={`flex justify-between ${step === 3 ? 'mt-4 pt-4 flex-shrink-0' : 'mt-8 pt-6'} border-t border-gray-200 dark:border-gray-600`}>
            <button
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/admin/pools/football')}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            <button
              onClick={() => {
                if (step === 3 && canProceedFromStep3) {
                  createPool()
                } else if (step === 1 && canProceedFromStep1) {
                  setStep(2)
                } else if (step === 2 && canProceedFromStep2) {
                  setStep(3)
                }
              }}
              disabled={
                loading ||
                (step === 1 && !canProceedFromStep1) ||
                (step === 2 && !canProceedFromStep2) ||
                (step === 3 && !canProceedFromStep3)
              }
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <LoadingSpinner />
              ) : step === 3 ? (
                'Create Pool'
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>
    </PoolAdminRoute>
  )
}
